import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Calendar,
  Settings, 
  LogOut,
  Menu,
  X,
  Layers,
  ChevronRight,
  Sparkles,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ChevronDown,
  Shield,
  BarChart2,
  ClipboardList,
  FileText,
  Inbox,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { hasPermission, APP, TAB, Clave } from '../../utils/authRoles';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  isCollapsed?: boolean;
  onCollapseToggle?: () => void;
}

interface SubMenuItem {
  name: string;
  href: string;
  icon?: any;
  description?: string;
  // Clave que el rol necesita para ver este ítem (ver PESTANAS en utils/authRoles.ts).
  permiso: Clave;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  color: string;
  description: string;
  disabled?: boolean;
  // Ítems sin sub-menú llevan su propia clave; los que tienen sub-menú se muestran si
  // queda al menos un sub-ítem visible.
  permiso?: Clave;
  subItems?: SubMenuItem[];
}

const navigation: NavigationItem[] = [
  { 
    name: 'Dashboard', 
    href: '/dashboard', 
    icon: Home, 
    color: 'from-blue-500 to-blue-600',
    description: 'Resumen general',
    permiso: TAB.DASHBOARD,
  },
  { 
    name: 'Personal', 
    href: '/personal', 
    icon: Users, 
    color: 'from-green-500 to-green-600',
    description: 'Gestión de empleados',
    subItems: [
      {
        name: 'Listado Personal',
        href: '/personal',
        icon: Users,
        description: 'Lista completa de empleados',
        permiso: TAB.PERSONAL_LISTADO,
      },
      {
        name: 'Estado Documentación',
        href: '/personal/estado-documentacion',
        icon: FileText,
        description: 'Control de documentos y vencimientos',
        permiso: TAB.PERSONAL_ESTADO_DOCUMENTACION,
      },
      {
        name: 'Pre-SISI',
        href: '/pre-sisi',
        icon: Inbox,
        description: 'Carga y aprobación de documentación',
        permiso: APP.PRESISI,
      }
    ]
  },
  { 
    name: 'Servicios', 
    href: '/servicios', 
    icon: Settings, 
    color: 'from-purple-500 to-purple-600',
    description: 'Carteras y nodos',
    subItems: [
      {
        name: 'Cobertura',
        href: '/servicios/cobertura',
        icon: BarChart2,
        description: 'Matriz de cobertura por servicio',
        permiso: TAB.SERVICIOS_COBERTURA,
      },
      {
        name: 'Visualización',
        href: '/servicios/visualizacion',
        icon: BarChart2,
        description: 'Matriz personal × clientes',
        permiso: TAB.SERVICIOS_VISUALIZACION,
      },
      {
        name: 'Servicios',
        href: '/servicios',
        icon: Settings,
        description: 'Carteras y nodos',
        permiso: TAB.SERVICIOS_SERVICIOS,
      },
      {
        name: 'Acreditación',
        href: '/acreditacion',
        icon: Shield,
        description: 'Acreditación de empresas',
        permiso: TAB.SERVICIOS_ACREDITACION,
      },
    ]
  },
  { 
    name: 'Programación', 
    href: '/planificacion', 
    icon: Calendar, 
    color: 'from-orange-500 to-orange-600',
    description: 'Planificación semanal',
    subItems: [
      {
        name: 'Planificación',
        href: '/planificacion',
        icon: Calendar,
        description: 'Planificación semanal',
        permiso: TAB.PROGRAMACION_PLANIFICACION,
      },
      {
        name: 'Resumen',
        href: '/planificacion/resumen',
        icon: ClipboardList,
        description: 'Personas y clientes asignados',
        permiso: TAB.PROGRAMACION_RESUMEN,
      },
    ]
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ 
  isOpen, 
  onToggle, 
  isCollapsed = false, 
  onCollapseToggle 
}) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  // Qué ve cada rol se configura en /configuracion/roles: cada ítem muestra su clave de
  // pestaña, y una sección con sub-menú desaparece si no le queda ningún sub-ítem. El
  // enlace a Pre-SISI depende del acceso a esa app (app.presisi), no de una pestaña.
  // El enlace de la sección apunta a su primer sub-ítem visible si el suyo quedó oculto
  // (ej. un rol sin "Listado de personal" pero con "Estado de documentación").
  const visibleNavigation = useMemo(() => navigation
    .map(item => {
      if (!item.subItems) return item;
      const subItems = item.subItems.filter(sub => hasPermission(user, sub.permiso));
      const href = subItems.some(sub => sub.href === item.href) ? item.href : subItems[0]?.href || item.href;
      return { ...item, href, subItems };
    })
    .filter(item => (item.subItems ? item.subItems.length > 0 : !item.permiso || hasPermission(user, item.permiso))),
  [user]);

  const toggleSubmenu = (itemName: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 bg-white/95 backdrop-blur-xl border-r border-gray-200/50 shadow-2xl transform transition-all duration-500 ease-out lg:translate-x-0 lg:static lg:inset-0 ${
          isCollapsed ? 'w-20' : 'w-72'
        } ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full max-h-screen">
          {/* Header */}
          <div className={`relative border-b border-gray-100 ${isCollapsed ? 'p-3' : 'p-6'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-center w-full">
                {/* Logo de la empresa */}
                <div className={`flex items-center justify-center ${isCollapsed ? 'w-20 h-20' : 'w-48 h-36'}`}>
                  <img
                    src={require('../../assets/LOGOF.png')}
                    alt="Logo Empresa"
                    className={`object-contain ${isCollapsed ? 'w-16 h-16' : 'w-48 h-36'}`}
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Botón de colapsar (solo desktop) */}
                {onCollapseToggle && (
                  <button
                    onClick={onCollapseToggle}
                    className="hidden lg:flex p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    title={isCollapsed ? 'Expandir menú' : 'Minimizar menú'}
                  >
                    {isCollapsed ? <ChevronRightIcon size={20} /> : <ChevronLeft size={20} />}
                  </button>
                )}
                {/* Botón de cerrar (solo móvil) */}
                <button
                  onClick={onToggle}
                  className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 py-6 space-y-1 overflow-y-auto ${isCollapsed ? 'px-2' : 'px-4'}`}>
            {visibleNavigation.map((item, index) => {
              const isActive = location.pathname === item.href;
              const hasSubItems = item.subItems && item.subItems.length > 0;
              const isExpanded = expandedItems[item.name];
              const isSubItemActive = hasSubItems && item.subItems?.some(sub => location.pathname === sub.href);
              
              const baseClasses = `group relative flex items-center rounded-xl transition-all duration-300 ease-out ${
                isCollapsed ? 'px-2 py-3 justify-center' : 'px-4 py-3'
              } ${
                isActive || isSubItemActive
                  ? 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200/50 shadow-sm' 
                  : 'hover:bg-gray-50 hover:shadow-sm'
              }`;

              // Si el item está deshabilitado, renderizamos un DIV visible pero no clicable
              if (item.disabled === true) {
                return (
                  <div key={item.name}>
                    <div
                      className={`${baseClasses} opacity-60 cursor-not-allowed`}
                      style={{ animationDelay: `${index * 100}ms` }}
                      title={isCollapsed ? item.name : undefined}
                      aria-disabled="true"
                    >
                      {isActive && !isCollapsed && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-500 rounded-r-full" />
                      )}

                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                        isActive 
                          ? `bg-gradient-to-br ${item.color} shadow-lg` 
                          : 'bg-gray-100'
                      }`}>
                        <item.icon 
                          size={20} 
                          className={`transition-colors duration-300 ${
                            isActive ? 'text-white' : 'text-gray-600'
                          }`} 
                        />
                      </div>

                      {!isCollapsed && (
                        <div className="ml-4 flex-1">
                          <div className={`font-semibold transition-colors duration-300 ${
                            isActive ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {item.name}
                          </div>
                          <div className={`text-xs transition-colors duration-300 ${
                            isActive ? 'text-gray-600' : 'text-gray-500'
                          }`}>
                            {item.description}
                          </div>
                        </div>
                      )}

                      {!isCollapsed && (
                        <ChevronRight 
                          size={16} 
                          className={`transition-all duration-300 ${
                            isActive 
                              ? 'text-blue-500 translate-x-0' 
                              : 'text-gray-400 -translate-x-1'
                          }`} 
                        />
                      )}
                    </div>
                  </div>
                );
              }

              // Item con submenu
              if (hasSubItems && !isCollapsed) {
                return (
                  <div key={item.name}>
                    <div className="flex items-center gap-1">
                      {/* Enlace principal clickeable */}
                      <Link
                        to={item.href}
                        onClick={(e) => {
                          if (window.innerWidth < 1024) {
                            onToggle();
                          }
                        }}
                        className={`${baseClasses} flex-1`}
                        style={{ animationDelay: `${index * 100}ms` }}
                        title={isCollapsed ? item.name : undefined}
                      >
                        {(isActive || isSubItemActive) && !isCollapsed && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-500 rounded-r-full" />
                        )}

                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                          isActive || isSubItemActive
                            ? `bg-gradient-to-br ${item.color} shadow-lg` 
                            : 'bg-gray-100 group-hover:bg-gray-200'
                        }`}>
                          <item.icon 
                            size={20} 
                            className={`transition-colors duration-300 ${
                              isActive || isSubItemActive ? 'text-white' : 'text-gray-600 group-hover:text-gray-800'
                            }`} 
                          />
                        </div>

                        <div className="ml-4 flex-1">
                          <div className={`font-semibold transition-colors duration-300 ${
                            isActive || isSubItemActive ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'
                          }`}>
                            {item.name}
                          </div>
                          <div className={`text-xs transition-colors duration-300 ${
                            isActive || isSubItemActive ? 'text-gray-600' : 'text-gray-500 group-hover:text-gray-600'
                          }`}>
                            {item.description}
                          </div>
                        </div>

                      </Link>
                      
                      {/* Botón de toggle para submenu */}
                      <button
                        onClick={() => toggleSubmenu(item.name)}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <ChevronDown 
                          size={16} 
                          className={`transition-all duration-300 ${
                            isExpanded ? 'rotate-180' : 'rotate-0'
                          } ${
                            isActive || isSubItemActive
                              ? 'text-blue-500' 
                              : 'text-gray-400 hover:text-gray-600'
                          }`} 
                        />
                      </button>
                    </div>

                    {/* Submenu items */}
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                        {item.subItems?.map((subItem) => {
                          const isSubActive = location.pathname === subItem.href;
                          return (
                            <Link
                              key={subItem.href}
                              to={subItem.href}
                              onClick={() => {
                                if (window.innerWidth < 1024) {
                                  onToggle();
                                }
                              }}
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                                isSubActive
                                  ? 'bg-blue-50 text-blue-700 font-medium'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              {subItem.icon && (
                                <subItem.icon 
                                  size={16} 
                                  className={isSubActive ? 'text-blue-600' : 'text-gray-400'}
                                />
                              )}
                              <div className="flex-1">
                                <div className="text-sm">{subItem.name}</div>
                                {subItem.description && (
                                  <div className="text-xs text-gray-500">{subItem.description}</div>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Item normal (sin submenu)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={baseClasses}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => {
                    if (window.innerWidth < 1024) {
                      onToggle();
                    }
                  }}
                  title={isCollapsed ? item.name : undefined}
                >
                  {isActive && !isCollapsed && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-500 rounded-r-full" />
                  )}

                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    isActive 
                      ? `bg-gradient-to-br ${item.color} shadow-lg` 
                      : 'bg-gray-100 group-hover:bg-gray-200'
                  }`}>
                    <item.icon 
                      size={20} 
                      className={`transition-colors duration-300 ${
                        isActive ? 'text-white' : 'text-gray-600 group-hover:text-gray-800'
                      }`} 
                    />
                  </div>

                  {!isCollapsed && (
                    <div className="ml-4 flex-1">
                      <div className={`font-semibold transition-colors duration-300 ${
                        isActive ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'
                      }`}>
                        {item.name}
                      </div>
                      <div className={`text-xs transition-colors duration-300 ${
                        isActive ? 'text-gray-600' : 'text-gray-500 group-hover:text-gray-600'
                      }`}>
                        {item.description}
                      </div>
                    </div>
                  )}

                  {!isCollapsed && (
                    <ChevronRight 
                      size={16} 
                      className={`transition-all duration-300 ${
                        isActive 
                          ? 'text-blue-500 translate-x-0' 
                          : 'text-gray-400 -translate-x-1 group-hover:translate-x-0 group-hover:text-gray-600'
                      }`} 
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className={`border-t border-gray-100 ${isCollapsed ? 'p-2' : 'p-4'} flex justify-center`}>
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className={`group ${isCollapsed ? 'p-2 w-12' : 'px-3 py-2 w-40'} rounded-xl transform transition-transform duration-200 ease-out cursor-pointer bg-gray-100 hover:bg-red-600 text-gray-700 hover:translate-x-0 hover:scale-105 hover:shadow-md`}
              title="Cerrar Sesión"
            >
              <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-center'}`}>
                <div className="w-8 h-8 bg-gray-300 rounded-lg flex items-center justify-center transition-transform duration-200 transform group-hover:bg-red-700 group-hover:scale-105">
                  <LogOut size={16} className="text-gray-700 group-hover:text-white" />
                </div>
                {!isCollapsed && (
                  <div className="ml-3">
                    <div className="text-sm font-medium">Cerrar Sesión</div>
                  </div>
                )}
              </div>
            </button>
          </div>

          {showLogoutConfirm && typeof document !== 'undefined' && createPortal(
            <div className="fixed inset-0 z-[60] flex items-center justify-center">
              <div className="absolute inset-0 bg-black/40" onClick={() => setShowLogoutConfirm(false)} />
              <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-8 relative z-10">
                <h3 className="text-lg font-semibold text-gray-800">¿Confirmas que quieres cerrar sesión?</h3>
                <p className="text-sm text-gray-500 mt-3">Se cerrará tu sesión actual y tendrás que iniciar sesión de nuevo.</p>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowLogoutConfirm(false)}
                    className="px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-base"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      setShowLogoutConfirm(false);
                      logout();
                    }}
                    className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-base"
                  >
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
    </>
  );
};
